import type { ReactNode } from "react";
import { fa } from "../i18n/fa";

/* ------------------------------------------------------------------ *
 * THE «حاکم» CHIP AND ITS CROWN — حکم's (`HokmTable`) until شلم P1 Task 3 (2026-10-10, R4), when
 * شلم's حاکم became their second user and both moved here, byte for byte. card-91's conditions for
 * the move: `hokm-hakem-chip.test.tsx` passes UNCHANGED; the chip's 10-09 sizes stay exactly as they
 * are (never re-derived); a table that needs another size or context gets a PROP whose default is
 * حکم's value — never a fork. `hakem-chip-move.test.tsx` pins the rendered markup against what
 * HokmTable drew before the move.
 *
 * THE NOTICE PILL joined them in شلم P1 Task 5 (2026-10-11): حکم's «{name} حاکم شد» toast, moved
 * here byte for byte (its DOM over 62 حکم states unchanged), its classes exported so شلم's first-
 * card announce «حکم: ♠ — {name}» wears the same gold pill rather than a copy of it.
 * ------------------------------------------------------------------ */

/** A crown drawn as inline SVG — the Hakem (حاکم) marker on both screens. */
export function CrownIcon({ size = 12 }: { size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M3 7l4.5 3.5L12 4l4.5 6.5L21 7l-1.8 11H4.8L3 7z" />
      <rect x="4.6" y="19.4" width="14.8" height="2.3" rx="1" />
    </svg>
  );
}

/** A crown + «حاکم» gold chip, shown above a Hakem seat and on the local player.
 *  NO WIDER THAN THE AVATAR (operator, 2026-10-08: «trim it on the left and right, make it slightly
 *  smaller overall»): 1 px each side, a 1 px gap, the word at 9 px and the crown at 10 px — 31.6 px
 *  over a zoomed screen's 32 px avatar (it was 51.6). 9 / 10 px is the floor for «recognisable at a
 *  glance»: never smaller. `hokm-hakem-chip.test.tsx` adds the parts up; the layout probe's
 *  `hakem-wide` measures it at every size.
 *  FOLLOW-UP (operator, 2026-10-09: «make it a little smaller top and bottom … the م sits almost
 *  against the edge, give it a small amount of space»): `leading-none` (18 → 13 px tall); the room
 *  goes to the word's end (`pe-[3px]`, the م's side in RTL), the crown keeps 1 px (its glyph has
 *  1.1 px of its own) with no gap, and the crown drops to 9 px to pay for it — still 31.6 px wide. */
export function HakemChip(): ReactNode {
  return (
    <span
      className="m-0 flex items-center gap-0 rounded-full bg-gradient-to-br from-secondary to-gold-deep py-0.5 pe-[3px] ps-px leading-none text-ink-on-gold shadow-md"
      style={{ fontSize: 9, fontWeight: 800 }}
    >
      <CrownIcon size={9} />
      {fa.hokm.hakem}
    </span>
  );
}

/** The notice pill's own classes — the gold toast, as حکم's «{name} حاکم شد» draws it. Exported so a
 *  table announcing another moment in the same voice (شلم's «حکم: ♠ — {name}») wears these classes
 *  rather than a copy of them. */
export const HAKEM_NOTICE_PILL =
  "m-0 flex items-center gap-1 rounded-full border border-gold-deep/60 bg-gradient-to-br from-secondary to-gold-deep px-2.5 py-0.5 text-ink-on-gold shadow-md";
/** …and its words' type: the caption, at 800. */
export const HAKEM_NOTICE_TEXT = "font-caption text-caption";
export const HAKEM_NOTICE_WEIGHT = 800;

/**
 * The compact Hakem-reveal toast — a small gold pill (crown + notice), sized like the
 * app's other on-screen alerts (the `StatusPill` tokens: `label-md`, tight padding) so
 * it announces the Hakem clearly WITHOUT dominating or covering the played cards.
 * Shared by the hand-1 selection ceremony reveal and the per-hand (hands 2+) announce;
 * the caller supplies the already grammatically-correct `notice` («شما حاکم شدید» when
 * it is you, «{name} حاکم شد» for another player), so the pill just renders it.
 */
export function HakemNoticePill({ notice }: { notice: string }): ReactNode {
  // Compact by request (2026-07-10): a small caption-sized chip — the crown +
  // gold already make it unmistakable without dominating the felt.
  return (
    <span className={HAKEM_NOTICE_PILL}>
      <CrownIcon size={11} />
      <span className={HAKEM_NOTICE_TEXT} style={{ fontWeight: HAKEM_NOTICE_WEIGHT }}>
        {notice}
      </span>
    </span>
  );
}
